import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import type { MediaQuota, MeResponse } from "@edu/shared";
import { useTeacherSubjects } from "../../lib/subjects.ts";
import { COVERAGE_SUBJECTS } from "../coverage/coverage-subjects.ts";
import * as api from "../../lib/api.ts";
import {
  ArchiveIcon,
  CardsIcon,
  CoverageIcon,
  GroupsIcon,
  HomeworkIcon,
  InviteIcon,
  KeyIcon,
  LessonsIcon,
  MediaIcon,
  MessagesIcon,
  NameTagIcon,
  PlansIcon,
  ScheduleIcon,
  SignOutIcon,
  SparkIcon,
  StudentsIcon,
  SubjectsIcon,
  ThemeIcon,
} from "./icons.tsx";
import { SideCredits } from "./SideCredits.tsx";

/**
 * Разделы кабинета и подвал: то, что раньше жило прямо в `Shell.tsx`.
 *
 * ⚠️ **Вынесено 15.09.2026 не ради длины файла, а потому что мест стало два.**
 * Ниже порога узкой раскладки сайдбара нет ни у кого (решение Р2,
 * `docs/phone-design-decisions.md`), и полный список разделов открывается листом
 * снизу по пункту «Ещё». Лист показывает ТЕ ЖЕ разделы и тот же подвал, то есть
 * вторая копия этой разметки разъехалась бы с первой на первой же правке и
 * разъехалась бы молча: тестов на компоненты в проекте нет и не планируется
 * (раздел 9 CLAUDE.md).
 *
 * Компонент отдаёт ФРАГМЕНТ, а не свою коробку: в сайдбаре список и подвал
 * обязаны быть прямыми детьми `.side`, иначе `margin-top: auto` у подвала
 * перестаёт прижимать его к нижней кромке. Коробку даёт тот, кто рисует: сайдбар
 * это сам `.side`, лист это `.sheet`.
 *
 * ⚠️ **Что НЕ переехало сюда: `.side__me`** (аватар с именем). Он шапка
 * сайдбара, а у листа шапка своя, с заголовком и «Закрыть».
 *
 * Разделы ровно те, у которых сегодня есть свой экран. Пустых пунктов не
 * заводим: пункт, ведущий в никуда, хуже отсутствующего. Уроки и ученики
 * разошлись на два раздела 14.08.2026: вперемешку на одной главной они мешали
 * друг другу, а нужны с разной частотой.
 */
export function SideNav(props: {
  me: MeResponse;
  /**
   * Сторона преподавателя. Считается в `Shell` через `isTeacherSide` и приезжает
   * готовым: правило одно на весь каркас, и второй его вычислитель разошёлся бы
   * с первым.
   */
  teacher: boolean;
  /** Сколько работ ученику задали и он их не открывал. */
  homework: number;
  /** Сколько новых сообщений у человека. */
  messages: number;
  /** Сколько записей «Что нового» он не читал. */
  whatsNew: number;
  /**
   * Карточки ученика: сколько показать сегодня, либо `null`, если пункта нет
   * (у человека нет ни одного преподавателя). Считает `useFlashcardsToday`.
   */
  flashcards: number | null;
  /** Пункт «Колоды» у преподавателя английского (`teachesEnglish`). */
  decks: boolean;
  /** Есть ли пункт «Сообщить о неточности»: условие считает провайдер заметки. */
  feedback: boolean;
  /**
   * Есть ли пункт «Позвать коллегу». Условие считает `Shell` одним выражением
   * (6.1 `docs/colleague-referral-design.md`); здесь своей проверки роли нет,
   * по той же причине, что у заметки.
   */
  referral: boolean;
  onTracking: () => void;
  onSubjects: () => void;
  onBoardName: () => void;
  onTheme: () => void;
  onPassword: () => void;
  onFeedback: () => void;
  onReferral: () => void;
  onSignOut: () => void;
  /**
   * Выход идёт: до него досылаются заметки и оценки карточек, и на плохой сети
   * это секунды. Кнопка говорит, что нажатие принято, и второй раз не жмётся.
   */
  leaving: boolean;
  /**
   * Нажали любой пункт. В сайдбаре не передаётся вовсе: там нажатие никуда
   * поверхность не девает. В листе «Ещё» этим он и закрывается, и закрывается
   * ДО того, как откроется окно подвала: два нативных `<dialog>` внахлёст
   * работают, но возвращают фокус лесенкой, а закрыть одно окно человеку проще,
   * чем два.
   */
  onPick?: (() => void) | undefined;
}) {
  const pick = () => props.onPick?.();
  const subjects = useTeacherSubjects();
  /** «Карта тем» есть, только если хоть у одного предмета человека есть карта. */
  const hasMap = COVERAGE_SUBJECTS.some((s) => subjects.includes(s));

  return (
    <>
      {/*
        Расписание первым и оно же домашняя страница, как в макете
        дизайнера. «Уроки» остаются вторым пунктом, а не заменяются им:
        это единственное место, где живут уроки без времени (ручные, старые,
        из пачки, осиротевшие правкой расписания), и убрав раздел, мы спрятали
        бы их насовсем. Макет писался до того, как в коде появились пачка
        уроков и раздел «Уроки».
      */}
      <ul className="side__nav">
        <li>
          <NavItem to="/schedule" label="Расписание" icon={<ScheduleIcon />} onPick={pick} />
        </li>
        <li>
          <NavItem to="/lessons" label="Уроки" icon={<LessonsIcon />} onPick={pick} />
        </li>
        {/*
          Домашка видна обеим ролям, как архив: ученик видит СВОИ работы
          (предикат по student_user_id стоит на сервере), преподаватель
          сводный список «кто из двенадцати сделал» (9.4 проекта домашки).
          ⚠️ Число у пункта это сколько работ ученику задали и он их не
          открывал (`HomeworkNews.tsx`). Транспорта уведомлений у продукта
          по-прежнему нет и раздел 4 CLAUDE.md его по-прежнему запрещает для
          критичного; это подпись в собственном интерфейсе, наружу не уходит
          ничего. До 20.08.2026 раздел был единственным местом, где ученик
          узнавал о заданном, то есть узнавал, только если сам догадался
          зайти. У преподавателя число всегда ноль, ветки по роли поэтому нет.
        */}
        <li>
          <NavItem
            to="/homework"
            label="Домашка"
            icon={<HomeworkIcon />}
            badge={props.homework}
            onPick={pick}
          />
        </li>
        {/*
          Сообщения видны обеим ролям, как домашка и архив: одна ручка отдаёт
          и отправленное, и пришедшее, а сторону считает сервер. Пункт стоит
          рядом с «Домашкой», потому что это второй раздел, куда ученик
          заходит по своей воле; всё, что ниже, преподавательское.

          ⚠️ **Число новых сообщений появилось 30.08.2026, и довод против него
          снят, а не забыт.** Здесь было написано, что числа нет намеренно,
          потому что считать его пришлось бы запросом ленты на каждом экране
          кабинета. Довод был верен ровно до появления отдельной дешёвой ручки
          счётчика: она возит три числа на разговор и ни одного тела, полторы
          тысячи байт на полусотне окон. Опрашивает её один таймер на вкладку
          (`ConversationNews.tsx`), а не каждый экран сам.

          ⚠️ Транспорта уведомлений у продукта по-прежнему нет и раздел 4
          CLAUDE.md его по-прежнему запрещает для критичного. Число это
          свежесть в ОТКРЫТОЙ вкладке: закрыл её, и продукт молчит. Ни одна
          строка экрана обратного не обещает.
        */}
        <li>
          <NavItem
            to="/announcements"
            label="Сообщения"
            icon={<MessagesIcon />}
            badge={props.messages}
            onPick={pick}
          />
        </li>
        {/*
          Карточки ученика (10.2 docs/flashcards-design.md). Число это сколько
          показать сегодня, опрос раз в десять минут, и оно, как у домашки,
          свежесть в ОТКРЫТОЙ вкладке, а не уведомление.
        */}
        {props.flashcards !== null && (
          <li>
            <NavItem
              to="/flashcards"
              label="Карточки"
              icon={<CardsIcon />}
              badge={props.flashcards}
              onPick={pick}
            />
          </li>
        )}
        {/*
          Ссылки и файлы, присланные преподавателем: только у стороны ученика.
          У преподавателя выданное живёт в карточке каждого ученика, а своего
          присланного ему не бывает. Иконка та же, что у медиатеки
          преподавателя: обе стороны её не видят разом никогда
        */}
        {!props.teacher && (
          <li>
            <NavItem to="/materials" label="Ссылки и файлы" icon={<MediaIcon />} onPick={pick} />
          </li>
        )}
        {props.teacher && (
          <>
            <li>
              <NavItem to="/students" label="Ученики" icon={<StudentsIcon />} onPick={pick} />
            </li>
            <li>
              <NavItem to="/groups" label="Группы" icon={<GroupsIcon />} onPick={pick} />
            </li>
            <li>
              <NavItem to="/plans" label="Заготовки" icon={<PlansIcon />} onPick={pick} />
            </li>
            <li>
              <NavItem to="/media" label="Материалы" icon={<MediaIcon />} onPick={pick} />
            </li>
            {/* Колоды карточек: только у того, кто ведёт английский (2.2 docs/flashcards-design.md) */}
            {props.decks && (
              <li>
                <NavItem to="/decks" label="Колоды" icon={<CardsIcon />} onPick={pick} />
              </li>
            )}
            {/*
              Карта тем: возможности платформы, а не данные учеников. Внутри
              два предмета с переключателем, математика по умолчанию.
              Только у преподавателя, тем же правилом, что «Ученики» и
              «Заготовки»: ученику ручка покрытия отвечает 404
            */}
            {hasMap && (
              <li>
                <NavItem to="/coverage" label="Карта тем" icon={<CoverageIcon />} onPick={pick} />
              </li>
            )}
          </>
        )}
        {/*
          Архив последним, как в карте навигации дизайнера: раздел отвечает на
          вопрос о прошлом, а остальные четыре нужны для сегодняшней работы.

          Пункт виден обеим ролям, и это не недосмотр. Ученик заходит сюда по
          той же ссылке и видит СВОИ занятия, и только пока преподаватель не
          закрыл доступ (`teacher_student.archive_visible`). Правило считает
          сервер тем же предикатом, что и экран самого урока, поэтому строки,
          на которой откажут, в списке не появится.
        */}
        <li>
          <NavItem to="/archive" label="Архив" icon={<ArchiveIcon />} onPick={pick} />
        </li>
      </ul>

      <div className="side__foot">
        {props.teacher && <SideQuota />}
        {/*
          Кредиты ИИ ниже хранилища и той же формой: оба это выданный человеку
          ресурс со своим знаменателем. Ученику не показываются оба, и ручка
          кредитов ему отвечает 404.
        */}
        {props.teacher && <SideCredits />}
        {/*
          Веса из общей системы тут нет намеренно: это пункт бокового меню, а
          не кнопка действия, и выглядеть он обязан как соседние пункты. Вид
          задаёт `.side__item`, и `btn` с весом рядом с ним был бы и враньём в
          разметке, и спором за цвет.
        */}
        {/*
          Учёт занятий: одна ручка на всех учеников. Пункт только у стороны
          преподавателя, тем же правилом, что «Ученики» и «Заготовки»: у
          ученика все экраны учёта живут в разделах, которых он не видит, и
          галочка здесь не включила бы ему ничего.
        */}
        {props.teacher && (
          <button
            type="button"
            className="side__item"
            title="Учёт занятий"
            onClick={() => {
              pick();
              props.onTracking();
            }}
          >
            <ScheduleIcon />
            <span className="side__label">Учёт занятий</span>
          </button>
        )}
        {/*
          Мои предметы: по ним прячутся инструменты чужих предметов. Только у
          стороны преподавателя, тем же правилом, что «Учёт занятий»: у ученика
          этих инструментов нет вовсе
        */}
        {props.teacher && (
          <button
            type="button"
            className="side__item"
            title="Мои предметы"
            onClick={() => {
              pick();
              props.onSubjects();
            }}
          >
            <SubjectsIcon />
            <span className="side__label">Мои предметы</span>
          </button>
        )}
        {/*
          «Что нового» в подвале, а не в списке разделов: разделы это места,
          куда человек уходит работать, а это справка, которую открывают раз в
          неделю. Пункт только у стороны преподавателя, тем же правилом, что
          «Ученики»: список написан для того, кто ведёт занятия.

          ⚠️ Условие `changelogSeenAt !== undefined` это не перестраховка.
          Отсутствие поля означает старый API, то есть счётчик, посчитанный
          неизвестно от чего; пункт с таким счётчиком хуже отсутствующего (тот
          же довод, что у `hasPassword` рядом).
        */}
        {props.teacher && props.me.changelogSeenAt !== undefined && (
          <NavItem
            to="/whats-new"
            label="Что нового"
            icon={<SparkIcon />}
            badge={props.whatsNew}
            onPick={pick}
          />
        )}
        {/*
          Подпись на доске. Пункт есть у ОБЕИХ сторон, в отличие от учёта и
          «Что нового»: курсор на доске есть и у ученика, и подписан ФИО из
          аккаунта он у него ровно так же.
        */}
        {/*
          ⚠️ У неполной сессии пункт погашен, но нажимается (9.6
          `docs/account-adopt-design.md`): `aria-disabled`, а не `disabled`,
          потому что по нажатию окно объясняет, почему подпись здесь не
          меняется. `disabled` нажатия не пропустил бы вовсе, и погашенный пункт
          без объяснения читался бы поломкой.
        */}
        <button
          type="button"
          className="side__item"
          title="Подпись на доске"
          aria-disabled={props.me.sessionPartial === true ? true : undefined}
          onClick={() => {
            pick();
            props.onBoardName();
          }}
        >
          <NameTagIcon />
          <span className="side__label">Подпись на доске</span>
        </button>
        {/*
          Светлая или тёмная. Пункт есть у ОБЕИХ сторон, как подпись на доске
          и по той же причине: это личная настройка браузера, а не работа с
          учениками, и ученик смотрит на те же экраны.

          ⚠️ Вторая дверь к этой же настройке стоит за шестерёнкой в комнате
          урока, и она важнее (решение владельца 02.09.2026): доска занимает
          весь экран урока, а выйти в кабинет посреди занятия нельзя. Дублем
          это не является: там нет ни сайдбара, ни окон кабинета.
        */}
        <button
          type="button"
          className="side__item"
          title="Светлая или тёмная"
          onClick={() => {
            pick();
            props.onTheme();
          }}
        >
          <ThemeIcon />
          <span className="side__label">Светлая или тёмная</span>
        </button>
        {/*
          Пункт есть только у того, у кого вход по паролю есть. Отсутствие
          признака (старый API, старый бандл) считаем «нет»: пункт, ведущий в
          404, хуже отсутствующего. Разбор в шапке `hasPassword` в
          packages/shared/src/user.ts.
        */}
        {props.me.hasPassword === true && (
          <button
            type="button"
            className="side__item"
            title="Сменить пароль"
            onClick={() => {
              pick();
              props.onPassword();
            }}
          >
            <KeyIcon />
            <span className="side__label">Сменить пароль</span>
          </button>
        )}
        {/*
          Заметка о неточности. Пункт стоит в подвале рядом со «Сменить
          пароль», а не в списке разделов: разделы это места, куда человек
          уходит работать, а это действие, после которого он остаётся там же,
          где был. Своего адреса у него поэтому нет, как и у смены пароля.

          Условие ровно одно и приходит из `enabled`: там уже сложены «живая
          сессия» и «сторона преподавателя». Своей проверки роли здесь нет
          намеренно, иначе правило разъехалось бы на два места, а точек входа
          у виджета четыре.

          ⚠️ Значок повторяет «Домашку», и это осознанный компромисс, а не
          недосмотр: `icons.tsx` этой правкой не трогается, а из десяти уже
          существующих фигур лист, на котором пишут, ближе всего к заметке.
          Путаницы в узкой полосе значков это не создаёт: подвал отделён от
          списка разделов и стоит у нижней кромки, а `title` есть у обоих
          пунктов. Своя фигура (например облачко с восклицательным знаком)
          это отдельная мелкая правка `icons.tsx`.
        */}
        {props.feedback && (
          <button
            type="button"
            className="side__item"
            title="Сообщить о неточности"
            onClick={() => {
              pick();
              props.onFeedback();
            }}
          >
            <HomeworkIcon />
            <span className="side__label">Сообщить о неточности</span>
          </button>
        )}
        {/*
          Позвать коллегу: рядом с заметкой и по той же логике. Это действие,
          после которого человек остаётся там, где был, а не раздел, поэтому в
          подвале и без своего адреса. На телефоне подвал живёт в листе «Ещё»,
          и пункт приезжает туда этим же компонентом.

          ⚠️ Признак это удобство, а не правило: сторону и «сессия не по ссылке
          ученика» держит сервер (4.1 `docs/colleague-referral-design.md`).
        */}
        {props.referral && (
          <button
            type="button"
            className="side__item"
            title="Позвать коллегу"
            onClick={() => {
              pick();
              props.onReferral();
            }}
          >
            <InviteIcon />
            <span className="side__label">Позвать коллегу</span>
          </button>
        )}
        <button
          type="button"
          className="side__item side__out"
          title="Выйти"
          disabled={props.leaving}
          aria-busy={props.leaving}
          onClick={() => {
            pick();
            props.onSignOut();
          }}
        >
          <SignOutIcon />
          <span className="side__label">{props.leaving ? "Выходим…" : "Выйти"}</span>
        </button>
      </div>
    </>
  );
}

/**
 * Пункт раздела.
 *
 * `NavLink` сам ставит `aria-current="page"` на активном адресе, и подсветка
 * висит именно на этом атрибуте, а не на своём классе: так состояние «я здесь»
 * видят и глаз, и читалка, и оно не может разойтись с адресом.
 *
 * `end` не нужен ни одному пункту: на корне разделов больше нет, он только
 * переадресует. У заготовок и материалов подсветка обязана гореть и на
 * вложенных экранах (редактор заготовки, нарезка документа), а это поведение
 * `NavLink` по умолчанию.
 */
function NavItem(props: {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Число у пункта. Ноль означает, что метки нет вовсе, а не «0» кружком. */
  badge?: number;
  onPick?: (() => void) | undefined;
}) {
  const badge = props.badge ?? 0;
  return (
    <NavLink
      className="side__item"
      to={props.to}
      /*
        Число уезжает и в `title`, потому что в свёрнутом виде сайдбара подписи
        нет вовсе, а кружок с цифрой без слова рядом не говорит, о чём он
      */
      title={badge === 0 ? props.label : `${props.label}: ${badge}`}
      onClick={() => props.onPick?.()}
    >
      {props.icon}
      <span className="side__label">{props.label}</span>
      {/*
        Метка стоит ПОСЛЕДНЕЙ в разметке и читается как часть пункта: «Домашка,
        3». Слова «новых» рядом с числом нет намеренно, его несёт `title`
        ссылки; дублировать его прятанным текстом значит завести класс для
        читалки, которого в проекте нет ни одного, ради одного места.

        ⚠️ Не `aria-live`: число меняется на переходах между экранами, и
        объявлять его вслух каждый раз значит перебивать чтение того экрана, на
        который человек только что пришёл.
      */}
      {badge > 0 && <span className="badge">{badge}</span>}
    </NavLink>
  );
}

/**
 * Полоска хранилища.
 *
 * Отдельной лёгкой ручкой `/v1/media/quota`, а не списком материалов: сайдбар
 * висит на всех экранах, и тянуть ради двух чисел всё дерево папок на канале
 * 128 кбит/с незачем.
 *
 * ⚠️ Отказ проглатывается молча и блок просто не рисуется. Это не лень: квота
 * здесь справка, а не работа, и красная строка «не удалось прочитать квоту» на
 * каждом экране кабинета говорила бы человеку о поломке, которой для его дела
 * нет. Заодно это делает выкатку безопасной: вкладка с новым бандлом на старом
 * API просто не покажет полоску.
 *
 * ⚠️ Ниже порога узкой раскладки компонент живёт в ДВУХ экземплярах: спрятанный
 * сайдбар со стенда не снимается (раскладку там решает CSS, а не React), и лист
 * «Ещё» приносит свою копию. Цена этого один лишний запрос на открытие листа, и
 * платится она сознательно: ветка по ширине в React означала бы состояние,
 * которое расходится с медиазапросом.
 */
function SideQuota() {
  const [quota, setQuota] = useState<MediaQuota | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .mediaQuota()
      .then((q) => {
        if (alive) setQuota(q);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  if (quota === null || quota.limitBytes === 0) return null;

  return (
    <div
      className="side__quota"
      title={`Хранилище: ${gb(quota.usedBytes)} из ${gb(quota.limitBytes)}`}
    >
      <div className="side__quota-head">
        <span>Хранилище</span>
        <span>
          {gb(quota.usedBytes)} / {gb(quota.limitBytes)}
        </span>
      </div>
      <progress max={quota.limitBytes} value={quota.usedBytes} />
    </div>
  );
}

/**
 * Гигабайты с одним знаком. Свой формат, а не `bytes` из материалов: тот живёт
 * в ленивом чанке материалов вместе с диалогами, и импорт ради одной строки
 * утащил бы его в стартовый чанк.
 */
function gb(value: number): string {
  return `${(value / 1024 / 1024 / 1024).toFixed(1).replace(".", ",")} ГБ`;
}
